import React from 'react';
import { NETWORK_OPTIONS, type Account } from '@mls-wallet/shared-types';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface MenuItemProps {
  onClick?: (e: React.MouseEvent) => void;
  disabled?: boolean;
  className?: string;
  children: React.ReactNode;
}

interface AccountMenuOptionsProps {
  account: Account;
  onAddNetworks?: (account: Account) => void;
  onEditName: (account: Account) => void;
  onActionComplete?: () => void;
  MenuItem: React.ComponentType<MenuItemProps>;
}

export function AccountMenuOptions({
  account,
  onAddNetworks,
  onEditName,
  onActionComplete,
  MenuItem,
}: AccountMenuOptionsProps) {
  // Use NETWORK_OPTIONS as the supported-network source of truth. An account
  // with every supported network cannot create a meaningful add-network proposal.
  const hasAllSupportedNetworks = NETWORK_OPTIONS.every((network) =>
    account.networks.includes(network.id)
  );

  const handleAddNetworks = (e: React.MouseEvent) => {
    e.stopPropagation();
    onAddNetworks?.(account);
    onActionComplete?.();
  };

  const handleEditName = (e: React.MouseEvent) => {
    e.stopPropagation();
    onEditName(account);
    onActionComplete?.();
  };

  return (
    <>
      {onAddNetworks ? (
        hasAllSupportedNetworks ? (
          <TooltipProvider delayDuration={0}>
            <Tooltip>
              <TooltipTrigger asChild>
                {/* Disabled Radix menu items do not receive pointer events, so
                   the wrapper owns hover/focus for the explanatory tooltip. */}
                <span className="block">
                  <MenuItem disabled>Add network</MenuItem>
                </span>
              </TooltipTrigger>
              <TooltipContent side="right" align="center">
                This account has already been deployed for all the supported
                networks.
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : (
          <MenuItem onClick={handleAddNetworks}>Add network</MenuItem>
        )
      ) : null}
      <MenuItem onClick={handleEditName}>Edit name</MenuItem>
    </>
  );
}
